import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import type { DocumentTrashSource } from "../../shared/document-trash";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { deleteDocument } from "./document-trash-api";

type Props = {
  slug?: string;
  source: DocumentTrashSource;
  id: string;
  revision: number;
  title: string;
  disabled?: boolean;
  onDeleted: () => void;
};
/** Inline confirmation also works inside the existing document sheet without stacking dialogs. */
export function DeleteDocument(props: Props) {
  const [confirming, setConfirming] = useState(false);
  const client = useQueryClient();
  const remove = useMutation({
    mutationFn: () => deleteDocument(props),
    onSuccess: async () => {
      props.onDeleted();
      client.removeQueries({ predicate: (query) => query.queryKey.includes(props.id) });
      await client.invalidateQueries({ queryKey: ["ops"] });
    },
  });
  if (!confirming)
    return (
      <Button
        variant="ghost"
        disabled={props.disabled}
        onClick={() => {
          setConfirming(true);
        }}
      >
        <Trash2 /> Delete
      </Button>
    );
  return (
    <div
      className="w-full space-y-3 rounded-lg bg-muted p-4"
      role="group"
      aria-label="Confirm document deletion"
    >
      <p className="text-sm">
        Move <strong>{props.title}</strong> to the recycle bin? You can restore it for 30 days.
        After that, it and its saved history are permanently deleted.
      </p>
      {props.source !== "internal" ? (
        <p className="text-sm text-muted-foreground">
          Customer access ends immediately. Any signed copy moves with the document. Billing stays
          unchanged.
        </p>
      ) : null}
      {remove.error ? <FieldError>{remove.error.message}</FieldError> : null}
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          variant="ghost"
          disabled={remove.isPending}
          onClick={() => {
            setConfirming(false);
          }}
        >
          Keep document
        </Button>
        <Button
          variant="destructive"
          disabled={(props.disabled ?? false) || remove.isPending}
          onClick={() => {
            remove.mutate();
          }}
        >
          {remove.isPending ? "Moving…" : "Move to recycle bin"}
        </Button>
      </div>
    </div>
  );
}
